import React, {useContext, useEffect, useRef, useState} from 'react';
import withLayout from '../../components/Layout/WithLayout';
import {FormSteps} from '../../config/formSteps';
import {TollRegistrationContext} from '../../context/ApplicationContext';
import TollAccountDetails from '../../components/TollAccountDetails';
import {useHistory} from 'react-router-dom';
import routes from '../../routes';
import TollProvider from '../../types/TollProvider';
import {InputValueAndError} from '../../types/InputValueAndError';
import tollReliefConfig from '../../config/TollReliefConfig';
import {
    collectErrorSummaryErrors,
    FormErrorAnchor,
    validateAlphaNumericAndSomeSymbols,
    validateEmailAddress
} from '../../utils/helpers';
import {Button, ComponentLoader, ErrorSummary} from '@snsw-gel/react';
import {ButtonsContainer, scrollToTop} from '@gaas/gaas-react-component-library';
import DefaultNoStepperLayout from '../../components/Layout/DefaultNoStepper';
import RegistrationFlowTermsAndConditions, {
    RegistrationFlowTermsAndConditionsHandle,
    tAndCFormInputDefs,
} from '../../components/RegistrationFlowTermsAndConditions';
import useTrConfig from '../../hooks/useTrConfig';
import {saveRegistrationApi, TollAccountFailure} from '../../services/api-services';
import ApiErrorAlert from './ApiErrorAlert';
import useTrRegistrationForm from '../../hooks/useTrRegistrationForm';
import TollAccountType from '../../types/tollAccountType';
import useStartTransaction from 'src/hooks/useStartTransaction';
import {userInfo} from 'src/utils/sessionStorage';
import {EventData, generateEventData} from 'src/utils/ga-page-tracking';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

const PersonalTollAccountDetailsPage: React.FC = () => {
    const tollAccountType = TollAccountType.Personal;

    const { isFeatureEnabled, isFeatureQueryLoading } = useBaseFeature();

    const trConfig = useTrConfig();
    const {
        productData,
        setProductData,
        setSuccessfullyLinkedAccountType,
    } = useContext(TollRegistrationContext);
    const history = useHistory();

    const [tollProvider, setTollProvider] =
        useState<InputValueAndError<TollProvider>>(new InputValueAndError<TollProvider>(null, (input) => { return [input.hasValue()] }));
    const [tollAccountNumber, setTollAccountNumber] =
        useState<InputValueAndError<string>>(new InputValueAndError('', (input) => { return [validateAlphaNumericAndSomeSymbols(input)] }));
    const [licensePlateNumber, setLicensePlateNumber] =
        useState<InputValueAndError<string>>(new InputValueAndError('', (input) => { return [validateAlphaNumericAndSomeSymbols(input) ]}));
    const [tollEmail, setTollEmail] =
        useState<InputValueAndError<string>>(new InputValueAndError('', (input) => { return [validateEmailAddress(input)] }));

    // TODO remove after Tr2 decom
    const [isTr2Decommissioned, setIsTr2Decommissioned] = useState<boolean | null>(null);
    const [agreeToTr2PersonalTerms, setAgreeToTr2PersonalTerms] =
        useState<InputValueAndError<boolean> | undefined> (new InputValueAndError<boolean>(null, (input) => { return [input.hasValue() && input.value] }));

    const [acceptNotifications, setAcceptNotifications] =
        useState<InputValueAndError<boolean>>(new InputValueAndError<boolean>(false, (input) => { return [input.hasValue()] }));
    const [agreeToTr3PersonalTerms, setAgreeToTr3PersonalTerms] =
        useState<InputValueAndError<boolean>>(new InputValueAndError<boolean>(null, (input) => { return [input.hasValue() && input.value] }));

    const transaction = useStartTransaction(history);
    const trRegistrationForm = useTrRegistrationForm()

    const termsAndConditionsComponentRef = useRef<RegistrationFlowTermsAndConditionsHandle>(null);
    const errorSummaryRef = useRef<HTMLInputElement | null>(null);

    /** For tracking when elig api is to be called. And displaying component loader. */
    const [isSaveRegistrationApiInProgress, setIsSaveRegistrationApiInProgress] = useState(false);

    /** If the user has tried to continue. Used for displaying error summary or not. */
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    /** For api errors */
    const [apiErrorReason, setApiErrorReason] = useState<TollAccountFailure | undefined>(undefined);

    /** Holds UI errors (not for bad api responses) */
    const formInputDefs = () => {
        const defs: FormErrorAnchor[] = [
            {
                inputValue: tollProvider,
                anchor: tollReliefConfig.anchors.tollProvider,
                message: 'Select a toll provider',
            },
            {
                inputValue: tollAccountNumber,
                anchor: tollReliefConfig.anchors.tollAccountNumber,
                message: 'Enter your toll account number',
            },
            {
                inputValue: licensePlateNumber,
                anchor: tollReliefConfig.anchors.licensePlateNumber,
                message: 'Enter your licence plate number',
            },
            {
                inputValue: tollEmail,
                anchor: tollReliefConfig.anchors.mainEmail,
                message: 'Enter the main email address used for your toll account',
            },
        ];
        if(!isFeatureQueryLoading && isTr2Decommissioned === false) {
            return defs.concat(tAndCFormInputDefs(trConfig, agreeToTr2PersonalTerms, agreeToTr3PersonalTerms))
        } else {
            return defs.concat(tAndCFormInputDefs(trConfig, undefined, agreeToTr3PersonalTerms))
        }
    }

    const formErrors = collectErrorSummaryErrors(formInputDefs());

    useEffect(() => {
        if(!isFeatureQueryLoading) {
            const isTr2Decommissioned = isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION);
            setIsTr2Decommissioned(isTr2Decommissioned);
            if(isTr2Decommissioned) {
                // so we don't validate tr2 T&C
                setAgreeToTr2PersonalTerms(undefined);
            }
        }
    }, [isFeatureQueryLoading]);

    const userInfoObj = userInfo();
    useEffect(() => {
        if (userInfoObj && userInfoObj.id) {
            // Simulating data for the event
            const eventData: EventData = {
                page_title: 'Link toll account - Personal',
                transaction_info: {
                    transaction_step: 'Toll account details',
                },
                user_info: {
                    user_id: userInfoObj.id,
                    user_login_status: 'fully authenticated',
                },
            };

            // Generating and pushing the event data to the dataLayer
            generateEventData(eventData);
        }
    }, [userInfoObj]);

    useEffect(() => {
        if (tollAccountType === undefined) {
            history.push(routes.manageTollAccount.path);
            return;
        }

        // initial productData
        setProductData({tollAccountType});

        /**
         * Set Gaas transaction
         */
        const initialiseTransaction = async () => {
            await transaction.initialise();
        }
        if (!transaction.isInitialised()) {
            initialiseTransaction();
        }
    }, [tollAccountType, history]);

    function onSave() {
        if (isSaveRegistrationApiInProgress) {
            return;
        }

        setHasAttemptedContinue(true);
        let valid = true;
        if (tollProvider.hasNoValue()) {
            valid = false;
            setTollProvider(tollProvider.withError());
        }

        if (tollAccountNumber.hasNoValue()) {
            valid = false;
            setTollAccountNumber(tollAccountNumber.withError());
        }

        if (licensePlateNumber.hasNoValue()) {
            valid = false;
            setLicensePlateNumber(licensePlateNumber.withError());
        }

        if (tollProvider.hasValue() && tollEmail.hasNoValue()) {
            valid = false;
            setTollEmail(tollEmail.withError());
        }

        const termsAndConditionsValid = termsAndConditionsComponentRef.current?.validate();
        if (valid && termsAndConditionsValid ) {
            const newProductData = {
                ...productData,
                tollAccountDetailsPageData: {
                    tollProvider: tollProvider.value!,
                    tollAccountNumber: tollAccountNumber.value!,
                    tollAccountEmail: tollEmail.value!,
                    lpn: licensePlateNumber.value!,
                },
                agreeTerms: {
                    agreeToTr2PersonalTerms: true,
                    agreeToTr3PersonalTerms: true,
                    // Explicitly overriding any sole trader terms they could have accepted on a different page.
                    agreeToTr2SoleTraderTerms: false,
                },
                acceptNotifications: acceptNotifications.value!,
            }
            setProductData(newProductData);
            setIsSaveRegistrationApiInProgress(true);

            saveRegistrationApi(trRegistrationForm.data(newProductData)).then((potentialFailure) => {
                if (potentialFailure === null) { // success
                    setSuccessfullyLinkedAccountType(tollAccountType);
                    transaction.clear();
                    history.push(routes.manageTollAccount.path);
                } else if (potentialFailure in TollAccountFailure) {
                    setApiErrorReason(potentialFailure as TollAccountFailure);
                    setProductData({tollAccountType: TollAccountType.Personal});
                    scrollToTop(); // TODO: this should focus on the error
                } else {
                    // system error
                    history.push('/error/500');
                }
            })
                .finally(() => {
                    setIsSaveRegistrationApiInProgress(false);
                });

        } else {
            errorSummaryRef.current?.focus();
        }

    }

    function onBack() {
        // Clear context
        setProductData({});
        history.push(routes.manageTollAccount.path);
    }

    return <>
        <ComponentLoader active={isSaveRegistrationApiInProgress || transaction.isLoading} fullPage label={tollReliefConfig.labels.componentLoader} />

        {/* Error summary - for frontend errors */}
        {hasAttemptedContinue && formErrors.length !== 0 && <ErrorSummary errors={formErrors} ref={errorSummaryRef}/>}

        {apiErrorReason !== undefined && <ApiErrorAlert errorReason={apiErrorReason} />}

        <TollAccountDetails
            tollAccountType={tollAccountType}
            tollProvider={tollProvider}
            setTollProvider={setTollProvider}
            tollAccountNumber={tollAccountNumber}
            setTollAccountNumber={setTollAccountNumber}
            licensePlateNumber={licensePlateNumber}
            setLicensePlateNumber={setLicensePlateNumber}
            tollEmail={tollEmail}
            setTollEmail={setTollEmail}
        />

        <RegistrationFlowTermsAndConditions
            ref={termsAndConditionsComponentRef}
            isSoleTraderRegistration={false}
            acceptNotifications={acceptNotifications}
            setAcceptNotifications={setAcceptNotifications}
            agreeToTr2PersonalTerms={agreeToTr2PersonalTerms}
            setAgreeToTr2PersonalTerms={setAgreeToTr2PersonalTerms}
            agreeToTr3PersonalTerms={agreeToTr3PersonalTerms}
            setAgreeToTr3PersonalTerms={setAgreeToTr3PersonalTerms}
        />

        <ButtonsContainer>
            <Button
                onClick={onSave}
                id='saveButtonPersonal'
            >
                Save
            </Button>
        </ButtonsContainer>

        <Button
            onClick={onBack}
            theme='back'
            id='backButton'
        >
            Back
        </Button>
    </>;
};

export default withLayout(PersonalTollAccountDetailsPage, DefaultNoStepperLayout, {
    step: FormSteps.TollAccountDetails,
    headerTitle: 'Link toll account',
});
